import { z } from 'zod';

export const AllowanceInputSchema = z.object({
  name: z.string().min(1).max(120),
  code: z.string().min(1).max(40),
  isTaxable: z.boolean().default(false),
  isFixed: z.boolean().default(true),
  defaultAmount: z.number().nonnegative().optional().nullable(),
  isActive: z.boolean().default(true),
});
export type AllowanceInput = z.infer<typeof AllowanceInputSchema>;

export const DeductionInputSchema = z.object({
  name: z.string().min(1).max(120),
  code: z.string().min(1).max(40),
  isFixed: z.boolean().default(true),
  defaultAmount: z.number().nonnegative().optional().nullable(),
  isActive: z.boolean().default(true),
});
export type DeductionInput = z.infer<typeof DeductionInputSchema>;

export const LoanInputSchema = z.object({
  employeeId: z.string().min(1),
  amount: z.number().positive(),
  monthlyDeduction: z.number().positive(),
  startDate: z.string().date(),
  endDate: z.string().date().optional().nullable(),
  reason: z.string().max(2000).optional().nullable(),
});
export type LoanInput = z.infer<typeof LoanInputSchema>;

// ─── Date-range bounds (P1-4) ─────────────────────────────────────────────────
//
// WHY A DATE RANGE NEEDS A CEILING AT ALL.
// `from`/`to` were `z.string().date()` with no span limit, and this one schema
// is `validate`d on EIGHT report endpoints. Downstream every one of them is an
// unbounded `findMany` with a joined employee and no `take`. So
// `GET /api/reports/overtime?from=1970-01-01&to=2099-12-31` was a legal,
// authenticated request that materialises every overtime day the company has
// ever had into a single JSON array on a single-VPS Node process — a
// self-inflicted DoS reachable by anyone holding a valid token, that typechecks
// perfectly. Pagination (P1-5) is the real fix for the payload size; this is the
// floor under it, and it costs one refinement.
//
// THE UI AUDIT BEHIND THE NUMBER (2026-08-15, apps/web). Every call site that
// constructs a range for these endpoints was read before the cap was chosen,
// because a real user hitting a 422 on a legitimate flow is a worse outcome
// than the DoS being closed:
//   - `routes/reports/useReportParams.ts` DATE_PRESETS — this month, last month,
//     last 7 days, last 30 days. Widest: 31 days.
//   - `routes/reports/workspace/ReportsWorkspace.tsx` presetRange() — mtd, last
//     month, 7d, 30d. Widest: 31 days.
//   - The free-form `<input type="date">` pairs (ReportScopeBar, the workspace's
//     "Custom…" pair, ComplianceReport). Unbounded before this work package —
//     they are the only producer that could reach the cap, and they are now
//     clamped to it client-side so nobody meets the 422 by hand.
//   - There is NO "this year" and NO "all time" preset anywhere in the app
//     (`grep -rn "all time\|this year\|ytd"` over apps/web/src: no hits).
//   - The annual/whole-employment surfaces do NOT take a range: /reports/gratuity
//     takes `employeeId` and derives joining→lastWorking server-side,
//     /reports/leave-balance takes `year`, /reports/timesheet takes `month`, and
//     the ERP CLOCKCARDSBATCH batch takes (year, month) validated by hand in
//     reports.routes.ts — none of them is bound by this schema and none is
//     affected by the cap.
// So the widest range the UI can legitimately PRODUCE is 31 days, and the cap
// sits an order of magnitude above it: a full leap year, inclusive, which is the
// widest window a hand-typed annual review ("1 Jan → 31 Dec") can ask for.
// Raise it only against a measured row count, not a hunch.
export const MAX_REPORT_RANGE_DAYS = 366;

const DAY_MS = 86_400_000;

/** A `from`/`to` pair as it travels on the wire: two YYYY-MM-DD strings. */
export interface DateRangeInput {
  from: string;
  to: string;
}

/**
 * Days covered by [from..to] INCLUSIVE — so a single day is 1, not 0. Inclusive
 * because that is what the existing house cap counts
 * (`CALENDAR_MAX_RANGE_DAYS`: 2026-01-01..2026-04-02 is 92), and two range
 * limits that count differently is how an off-by-one becomes a support ticket.
 *
 * Parsed at UTC midnight, never through the local zone: `new Date('2026-03-01')`
 * west of Greenwich lands on the previous day, which would make the span wrong
 * by one for half the world.
 *
 * `NaN` when either end is unparseable. Callers treat that as "not my error" —
 * `z.string().date()` has already rejected it.
 */
export function rangeSpanDays(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return Number.NaN;
  return Math.round((b - a) / DAY_MS) + 1;
}

/**
 * THE REFINEMENT. Apply with `.superRefine(boundedDateRange(N))` to any schema
 * carrying a `from`/`to` day pair.
 *
 * Two distinct failures, deliberately not collapsed into one:
 *   - RANGE_INVERTED  — `to` before `from`. Today this silently returns zero
 *     rows, which reads as "nothing happened" rather than "you asked backwards".
 *   - RANGE_TOO_LARGE — a span wider than the endpoint family's cap.
 * The code travels in `params.code` so a client can branch on it; the MESSAGE
 * names the limit in days, because that is the half a human reads. Both surface
 * as 422 through the existing ZodError branch of the API's error middleware —
 * no new error plumbing exists for this, by design.
 *
 * The issue is pathed at `to` so a form highlights the field the user can
 * actually move, rather than the whole query object.
 */
export function boundedDateRange(
  maxDays: number
): (value: DateRangeInput, ctx: z.RefinementCtx) => void {
  return (value, ctx) => {
    const span = rangeSpanDays(value.from, value.to);
    // Unparseable ends are already a format error from `z.string().date()`;
    // adding a second issue for the same typo would only be noise.
    if (Number.isNaN(span)) return;
    if (span < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        params: { code: 'RANGE_INVERTED' },
        message: '`to` must not be earlier than `from`.',
      });
      return;
    }
    if (span > maxDays) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        params: { code: 'RANGE_TOO_LARGE' },
        message: `Date range must be ${maxDays} days or fewer (asked for ${span}).`,
      });
    }
  };
}

/**
 * The same limit, applied in the UI so the picker cannot OFFER an illegal range.
 * One definition serving both sides is the whole point of the shared-schema
 * seam: the 422 is the backstop, not the user experience.
 *
 * `anchor` is the edge the user just touched and which therefore must not move —
 * edit `from` and the far end slides, edit `to` and the near end does. An
 * inverted pair collapses onto the anchor rather than being silently swapped,
 * because swapping changes what was asked for without saying so.
 */
export function clampDateRange(
  range: DateRangeInput,
  maxDays: number,
  anchor: 'from' | 'to' = 'from'
): DateRangeInput {
  const span = rangeSpanDays(range.from, range.to);
  if (Number.isNaN(span)) return range;
  if (span >= 1 && span <= maxDays) return range;
  const shift = span < 1 ? 0 : maxDays - 1;
  // Stepped on the CALENDAR (setUTCDate on a UTC-midnight day key), not by
  // adding 24-hour spans — the day-key idiom check:datebasis asks for.
  const step = (ymd: string, days: number): string => {
    const d = new Date(`${ymd}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  };
  if (anchor === 'from') return { from: range.from, to: step(range.from, shift) };
  return { from: step(range.to, -shift), to: range.to };
}


export const TimesheetQuerySchema = z.object({
  employeeId: z.string().optional(),
  month: z.string().regex(/^\d{4}-\d{2}$/, 'Month must be YYYY-MM'),
});
export type TimesheetQuery = z.infer<typeof TimesheetQuerySchema>;

// ─── Worker × Site report ─────────────────────────────────────────────────────
// One row per worker per date per SITE, surfacing the per-site clock+minutes the
// AttendanceDaySite split already computes but no report exposed. Reuses
// ReportRangeSchema (from/to + optional worksiteId) for the query; this is the
// JSON row shape the GET /api/reports/worker-site handler returns.
export const WorkerSiteReportRowSchema = z.object({
  employeeId: z.string(),
  employeeCode: z.string(),
  employeeName: z.string(),
  date: z.string(), // ISO date (YYYY-MM-DD)
  worksiteId: z.string().nullable(),
  worksiteName: z.string(),
  worksiteCode: z.string().nullable(),
  firstInAt: z.string().nullable(), // ISO datetime
  lastOutAt: z.string().nullable(), // ISO datetime
  grossMinutes: z.number().int(),
  breakMinutes: z.number().int(),
  netMinutes: z.number().int(),
  workedMinutes: z.number().int(),
  otMinutes: z.number().int(),
  // Review markers, not measurements. `autoClosed` means the day ended with a
  // dangling SITE punch-in and the close time was INVENTED by the auto-close
  // job; `misBilledClose` means this site's interval was cut short because the
  // worker appeared at a DIFFERENT site without punching out here. Either way
  // the minutes on this row are an estimate, and a row that does not say so
  // reads as an observation. Present on the leaf so a disputed figure can be
  // traced to the punch that caused it.
  autoClosed: z.boolean(),
  misBilledClose: z.boolean(),
});
export type WorkerSiteReportRow = z.infer<typeof WorkerSiteReportRowSchema>;

// ─── Worksite hours (period totals) ───────────────────────────────────────────
// The human-readable companion to the ERP CLOCKCARDSBATCH export: "how many
// hours did each project record, and whose hours were they?" — in RAW HOURS,
// with no day conversion and no divisor. The export compresses the same data
// into NT/OT1/OT2; this is what you check the period against BEFORE posting.
// Spec: docs/erp-clockcardsbatch-export-spec-2026-08-08.md §9.
//
// The three buckets mirror the ERP's columns exactly, so a number here can be
// traced straight to a column there:
//   normalMinutes — regular (non-OT) time on ORDINARY working days
//   ot1Minutes    — overtime on ORDINARY working days      (the 1.25x band)
//   ot2Minutes    — ALL hours worked on a rest day/holiday (the 1.5x band)
// A rest day is premium for the WHOLE day (decision C1), so none of its time
// appears under normalMinutes — it is all ot2. `daysCovered` still counts it:
// a rest day worked earns the day AND the premium, it is not either/or.
export const WorksiteHoursTotalsSchema = z.object({
  normalMinutes: z.number().int(),
  ot1Minutes: z.number().int(),
  ot2Minutes: z.number().int(),
  /** Distinct calendar dates with any recorded presence. */
  daysCovered: z.number().int(),
});

export const WorksiteHoursByWorksiteRowSchema = WorksiteHoursTotalsSchema.extend({
  worksiteId: z.string().nullable(),
  worksiteName: z.string(),
  worksiteCode: z.string().nullable(),
  /** Distinct people who recorded time here in the period. */
  workers: z.number().int(),
  /** Worked minutes on ordinary days above the standard day but below the
   *  overtime threshold: in neither column, exactly as the ERP file leaves
   *  them. Stated so normal + OT1 + OT2 is not read as everything worked. */
  uncountedMinutes: z.number().int(),
});
export type WorksiteHoursByWorksiteRow = z.infer<typeof WorksiteHoursByWorksiteRowSchema>;

export const WorksiteHoursByPersonRowSchema = WorksiteHoursTotalsSchema.extend({
  employeeId: z.string(),
  employeeCode: z.string(),
  employeeName: z.string(),
  /** SITE_WORKER / OFFICE_STAFF — the report covers both populations. */
  workforceType: z.string(),
  worksiteId: z.string().nullable(),
  worksiteName: z.string(),
  worksiteCode: z.string().nullable(),
});
export type WorksiteHoursByPersonRow = z.infer<typeof WorksiteHoursByPersonRowSchema>;

// ─── ERP CLOCKCARDSBATCH batch ────────────────────────────────────────────────
// The month-end upload for the client's ERP (BuildSmart/Candy). MIXED UNITS in
// one row: `nt` is DAYS (fractional, 3 dp), `ot1`/`ot2` are HOURS. See
// docs/erp-clockcardsbatch-export-spec-2026-08-08.md.
export const ClockcardsRowSchema = z.object({
  prl: z.string(),
  empCode: z.string(),
  contract: z.string(),
  activity: z.string(),
  glCode: z.string(),
  week: z.number(),
  /** The ERP's DAYNO column, which carries the calendar MONTH — not a day. */
  dayNo: z.number(),
  useRate: z.number(),
  nt: z.number(),
  ot1: z.number(),
  ot2: z.number(),
  ot3: z.number(),
  ot4: z.number(),
  ot5: z.number(),
});
export type ClockcardsRow = z.infer<typeof ClockcardsRowSchema>;

// A row that IS in the file but has a blank required column, and why. Nothing
// is ever dropped from the batch (client decision 2026-08-09) — the ERP decides
// how to treat an incomplete row. These are surfaced so the gap is known BEFORE
// the upload rather than discovered as a rejection afterwards.
export const ClockcardsWarningSchema = z.object({
  employeeId: z.string(),
  employeeCode: z.string(),
  employeeName: z.string(),
  reason: z.enum(['no-payroll-number', 'no-worksite', 'no-contract-code', 'no-gl-code']),
  detail: z.string(),
  workedMinutes: z.number().int(),
});
export type ClockcardsWarning = z.infer<typeof ClockcardsWarningSchema>;

export const ClockcardsBatchSchema = z.object({
  year: z.number().int(),
  month: z.number().int(),
  payrollNumber: z.string().nullable(),
  rows: z.array(ClockcardsRowSchema),
  warnings: z.array(ClockcardsWarningSchema),
  totals: z.object({
    rowCount: z.number().int(),
    employeeCount: z.number().int(),
    nt: z.number(),
    ot1Hours: z.number(),
    ot2Hours: z.number(),
    warningCount: z.number().int(),
    incompleteWorkedMinutes: z.number().int(),
  }),
  availablePayrolls: z.array(z.string()),
});
export type ClockcardsBatch = z.infer<typeof ClockcardsBatchSchema>;

export const ReportFormatSchema = z.object({
  format: z.enum(['json', 'excel']).default('json'),
});
export type ReportFormat = z.infer<typeof ReportFormatSchema>;

// ─── Manpower trend (over time) ───────────────────────────────────────────────
// Per site/camp, per DAY over a date range: headcount on the books
// (Employee/WorksiteAssignment), present/late/absent/on-leave + worked/OT
// minutes (from AttendanceDay/AttendanceDaySite). Fills the gap that site/camp
// manpower only shows a single-day snapshot. Query reuses ReportRangeSchema (from/to +
// optional worksiteId/departmentId). One row per (date, worksite); a NULL site
// surfaces as the "Unassigned" bucket (worksiteId null, worksiteName label).
export const ManpowerTrendRowSchema = z.object({
  date: z.string(), // ISO date (YYYY-MM-DD)
  worksiteId: z.string().nullable(),
  worksiteName: z.string(),
  assignedCount: z.number().int(),
  presentCount: z.number().int(),
  lateCount: z.number().int(),
  absentCount: z.number().int(),
  onLeaveCount: z.number().int(),
  /**
   * ON THE WIRE BECAUSE THE RATE DIVIDES BY IT.
   *
   * The server has always counted half-days in the attendance denominator and
   * never sent the figure, so the chart above the table — which re-derives the
   * rate per date, across sites — could not reach the same answer even in
   * principle. Present 10 / late 2 / absent 3 / half 5 read 50.0% in the table
   * and 66.7% on the line directly above it, always biased high, with nothing
   * on screen saying the two were computed differently.
   */
  halfDayCount: z.number().int(),
  /**
   * present / (present + late + absent + halfDay) * 100, or NULL when that
   * denominator is zero — a rest day, a holiday, a site nobody was rostered to.
   * A rate over nobody is not 0%: rule 3.3, and 0 here drew a cliff to the
   * floor on the trend line, indistinguishable from a real company-wide
   * no-show. Compute it with `attendanceRateFromCounts` rather than inline; a
   * second implementation of a rate is how the chart and the table drifted.
   */
  attendancePct: z.number().nullable(),
  totalWorkedMinutes: z.number().int(),
  totalOvertimeMinutes: z.number().int(),
});
export type ManpowerTrendRow = z.infer<typeof ManpowerTrendRowSchema>;

/**
 * THE attendance rate — one implementation, shared by the server that computes
 * the manpower-trend rows and the client that re-aggregates them per date.
 *
 * Present over everyone the day counted for: present, late, half-day and
 * absent. `onLeave` is deliberately outside it — a worker on approved leave was
 * not expected, and counting them absent would punish the roster for its own
 * approvals.
 *
 * Returns `null` for an empty denominator. Callers must render that as a dash
 * or a gap, never as 0.
 */
export function attendanceRateFromCounts(counts: {
  presentCount: number;
  lateCount: number;
  absentCount: number;
  halfDayCount: number;
}): number | null {
  const denom =
    counts.presentCount + counts.lateCount + counts.absentCount + counts.halfDayCount;
  if (denom <= 0) return null;
  return Math.round((counts.presentCount / denom) * 1000) / 10;
}

// ─── Document & visa expiry (bucketed) ────────────────────────────────────────
// Upcoming compliance-document expiries (passport, Emirates ID, visa, labour
// card) bucketed by threshold, one row per (employee, document). EXPIRED =
// expiryDate before today; D30/D60/D90 = within +30/+60/+90 days (mutually
// exclusive). Query: `days` (default 90, the furthest bucket) + role scope.
export const DocumentExpiryBucketSchema = z.enum([
  'EXPIRED',
  'D30',
  'D60',
  'D90',
]);
export type DocumentExpiryBucket = z.infer<typeof DocumentExpiryBucketSchema>;

export const DocumentExpiryRowSchema = z.object({
  employeeId: z.string(),
  employeeCode: z.string(),
  employeeName: z.string(),
  documentType: z.string(), // DocumentType enum (PASSPORT/EMIRATES_ID/VISA/LABOR_CARD)
  documentName: z.string().nullable(),
  expiryDate: z.string(), // ISO date (YYYY-MM-DD)
  daysUntilExpiry: z.number().int(), // negative when already expired
  bucket: DocumentExpiryBucketSchema,
});
export type DocumentExpiryRow = z.infer<typeof DocumentExpiryRowSchema>;

// Flat counts for the dashboard widget. The existing flat document-expiry report
// is untouched; this is a separate summary keyed by the same buckets.
export const DocumentExpirySummarySchema = z.object({
  expired: z.number().int(),
  within30: z.number().int(),
  within60: z.number().int(),
  within90: z.number().int(),
});
export type DocumentExpirySummary = z.infer<
  typeof DocumentExpirySummarySchema
>;

// ─── Exec overview dashboard ──────────────────────────────────────────────────
// Single consolidated KPI payload for the executive overview page (ADMIN/HR).
// Each block links to its detail report on the web. Mirrors the per-report row
// schemas above so the web client reads one typed payload.
export const ExecOverviewOtTrendPointSchema = z.object({
  date: z.string(), // ISO date (YYYY-MM-DD)
  overtimeMinutes: z.number().int(),
});
export type ExecOverviewOtTrendPoint = z.infer<
  typeof ExecOverviewOtTrendPointSchema
>;

export const ExecOverviewSiteManpowerSchema = z.object({
  worksiteId: z.string().nullable(),
  worksiteName: z.string(),
  presentCount: z.number().int(),
});
export type ExecOverviewSiteManpower = z.infer<
  typeof ExecOverviewSiteManpowerSchema
>;

export const ExecOverviewPayloadSchema = z.object({
  /** On the roll — active, probation, notice period — in scope. */
  totalHeadcount: z.number().int(),
  /**
   * The Overview's rate: present / expected on the site-expected rule
   * (`expectedToday.ts`), else present / scheduled. NULL when there is neither
   * — a rest day, an empty scope — and the tile renders a dash, not "0%" (3.3).
   */
  todayAttendanceRatePct: z.number().nullable(),
  /** Which denominator the rate divided by, so the caption names it. */
  rateBasis: z.enum(['scheduled', 'due']).nullable(),
  presentToday: z.number().int(),
  scheduledToday: z.number().int(),
  /** Expected at a site today, less leave and rest — the Overview's "N expected". */
  dueToday: z.number().int(),
  /** Of `dueToday`, checked in anywhere — the rate's numerator on the `due` basis. */
  duePresentToday: z.number().int(),
  otTrend: z.array(ExecOverviewOtTrendPointSchema), // last 7 days
  manpowerPerSite: z.array(ExecOverviewSiteManpowerSchema), // today, camps excluded
  /**
   * Distinct employees who clocked in at ANY site today. NOT the sum of
   * `manpowerPerSite[].presentCount` — `AttendanceDaySite` is one row per
   * employee PER SITE per day, so anyone transferred between sites appears in
   * two buckets and summing them counts them twice. This is the headcount.
   */
  presentOnSiteToday: z.number().int(),
  /** The documents report's rows: expired, or due within its 60-day horizon. */
  upcomingExpiryCount: z.number().int(),
});
export type ExecOverviewPayload = z.infer<typeof ExecOverviewPayloadSchema>;
